import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  UserContractCategoriesParams,
  UserContractCategoriesRequestData,
  UserContractCategory,
  VotingPanelMembershipStatusRequestPayload
} from '../../UserContractCategories/types'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import { useCRUDUserPopupProviderContext } from '../../../pages/CRUDUser/Provider/CRUDUserPopupProvider'
import { useDispatch } from 'react-redux'
import { setLoading } from '../../../redux/store/common/slice'
import {
  getHospitalUserContractCategories,
  setupContractCategoryVotingPanelMembershipStatus
} from '../../UserContractCategories/api'
import { OnChangeVotingPanelMembershipProps, columns } from './constants'
import { notification } from 'components/Notification'
import { useCRUDUserContext } from 'pages/CRUDUser/Provider/CRUDUserProvider'

type THospitalsCCTableProps = {
  userId: string
  activeKey: string
  setSelectedRowKeys: Dispatch<SetStateAction<string[]>>
  setSingleSelectedCC: Dispatch<SetStateAction<string | null>>
  canSelectCC?: boolean
  updateCallback?: () => void
  canViewVotingPanelMembership?: boolean
  canUpdateVotingPanelMembership?: boolean
}

export const useHospitalsCCTable = ({
  userId,
  activeKey,
  setSelectedRowKeys,
  setSingleSelectedCC,
  canSelectCC,
  updateCallback,
  canViewVotingPanelMembership,
  canUpdateVotingPanelMembership
}: THospitalsCCTableProps) => {
  const [totalNumber, setTotalNumber] = useState(0)
  const [searchValue, setSearchValue] = useState<string>('')
  const [sortParams, setSortParams] =
    useState<UserContractCategoriesRequestData>()
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [tableData, setTableData] = useState<UserContractCategory[]>([])

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const { deleteCCPopup } = useCRUDUserPopupProviderContext()

  const {
    state: {
      canViewVotingPanelMembership: canViewVotingPanelMembershipFromContext,
      canUpdateVotingPanelMembership: canUpdateVotingPanelMembershipFromContext
    }
  } = useCRUDUserContext()

  const resolvedCanViewVotingPanelMembership =
    canViewVotingPanelMembership ?? canViewVotingPanelMembershipFromContext
  const resolvedCanUpdateVotingPanelMembership =
    canUpdateVotingPanelMembership ?? canUpdateVotingPanelMembershipFromContext

  const dispatch = useDispatch()

  const getContractCategories = useCallback(
    async ({
      sortField,
      sortOrder,
      page,
      search
    }: UserContractCategoriesRequestData) => {
      try {
        dispatch(setLoading(true))
        const params: UserContractCategoriesParams = {}
        if (sortField) {
          if (sortOrder) {
            params.ordering = `${
              sortOrder === 'descend' ? '-' : ''
            }${sortField}`
          }
          setSortParams({
            sortField,
            sortOrder
          })
        }
        if (search) {
          params.search = search
        }
        const dataPage = page ?? pageInfo
        params.limit = dataPage.pageSize
        params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
        const { data } = await getHospitalUserContractCategories({
          userId: userId,
          hospitalId: activeKey,
          params
        })
        setTableData(
          data.results.map((i) => ({
            ...i,
            key: i.uuid
          }))
        )
        setTotalNumber(data.count)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, pageInfo, userId, activeKey]
  )

  useEffect(() => {
    if (activeKey) {
      setSearchValue('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey])

  useEffect(() => {
    if (activeKey) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        setPageInfo({ ...DEFAULT_PAGE })
        getContractCategories({
          ...sortParams,
          page: { ...DEFAULT_PAGE },
          search: searchValue
        })
      }, 500)
      setTableData([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, searchValue])

  const handleChangePageSize = useCallback(
    async (pageSize) => {
      const page = {
        pageNumber: 1,
        pageSize
      }
      await getContractCategories({
        ...sortParams,
        page,
        search: searchValue
      })
      setPageInfo(page)
    },
    [getContractCategories, searchValue, sortParams]
  )

  const handleTableChange = useCallback(
    async (pagination, _filters, sorter) => {
      const page = {
        pageNumber: pagination.current,
        pageSize: pagination.pageSize
      }
      await getContractCategories({
        sortField: sorter.field,
        sortOrder: sorter.order,
        page,
        search: searchValue
      })
      setPageInfo(page)
    },
    [getContractCategories, searchValue]
  )

  const handleSelectRow = (selectedRowKeys) =>
    setSelectedRowKeys(selectedRowKeys)

  const onDeleteCC = useCallback(
    (id: string) => {
      setSingleSelectedCC(id)
      deleteCCPopup.actions.open()
    },
    [setSingleSelectedCC, deleteCCPopup]
  )

  const onUpdateCCList = useCallback(async () => {
    getContractCategories({
      ...sortParams,
      page: pageInfo,
      search: searchValue
    })
    updateCallback?.()
  }, [getContractCategories, sortParams, pageInfo, searchValue, updateCallback])

  const onChangeVotingPanelMembership = useCallback(
    async ({ ccId, name, value }: OnChangeVotingPanelMembershipProps) => {
      try {
        dispatch(setLoading(true))
        const data: VotingPanelMembershipStatusRequestPayload = {
          contract_categories: [
            {
              contract_category: ccId,
              is_voting_panel_member: value
            }
          ]
        }
        await setupContractCategoryVotingPanelMembershipStatus({
          userId,
          hospitalId: activeKey,
          data
        })
        notification.success({
          message: `Successfully updated the voting panel membership status for the contract category ${name}.`
        })
        await onUpdateCCList()
      } catch (err) {
        notification.error({
          message: 'Failed to update voting panel membership status'
        })
        dispatch(setLoading(false))
      }
    },
    [dispatch, onUpdateCCList, userId, activeKey]
  )

  const tableColumns = useMemo(() => {
    return columns(
      !!canSelectCC,
      onDeleteCC,
      onChangeVotingPanelMembership,
      resolvedCanViewVotingPanelMembership,
      resolvedCanUpdateVotingPanelMembership
    )
  }, [
    canSelectCC,
    onChangeVotingPanelMembership,
    onDeleteCC,
    resolvedCanViewVotingPanelMembership,
    resolvedCanUpdateVotingPanelMembership
  ])

  return {
    tableColumns,
    tableData,
    totalNumber,
    pageInfo,
    handleChangePageSize,
    handleSelectRow,
    handleTableChange,
    onUpdateCCList,
    searchValue,
    setSearchValue
  }
}
